import { Button } from "@components/buttons/button";
import { Card } from "@components/cards/card";
import { Input } from "@components/forms/input";
import { Select } from "@components/forms/select";
import { ModalRaffleAffiliateCreate } from "@components/modals/modal-raffle-affiliate-create";
import { ModalRaffleAffiliateEdit } from "@components/modals/modal-raffle-affiliate-edit";
import { ModalRaffleAffiliateSummary } from "@components/modals/modal-raffle-affiliate-summary";
import { Pagination } from "@components/pagination";
import { RafflePrimaryInfo } from "@components/raffles/owner/primaryInfo";
import { LoadingSkeleton } from "@components/skeleton-loading";
import {
  IRaffleDefaultTicketPurchaseListDTO,
  IRaffleDefaultTicketPurchaseSummaryDTO,
} from "@hooks/dtos/raffles/IRaffleDefaultTicketPurchaseDTO";
import {
  RaffleDefaultTicketPurchaseStatusEnum,
  RaffleDefaultTicketPurchaseStatusTypes,
} from "@hooks/enums/raffle_default_ticket_purchase";
import { abbreviateName, formatName } from "@hooks/functions/formatName";
import { goToHome } from "@hooks/functions/General";
import { Masks } from "@hooks/functions/Masks/Masks";
import {
  getRaffleDefaultTicketPurchaseStatus,
  raffleDefaultTicketPurchaseStatusSelect,
} from "@hooks/functions/Raffle/status";
import { ZuToastifyStore } from "@hooks/stores/zustand-toastify-store";
import { api } from "@service/api";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FaPlus } from "react-icons/fa";
import { FaBroom, FaCircleInfo, FaExpand, FaMinus } from "react-icons/fa6";
import { useSearchParams } from "react-router-dom";

interface IPurchaseSummaryParams {
  startAt?: string;
  endAt?: string;
}

interface IPurchaseHistoryParams {
  page?: number;
  startAt?: string;
  endAt?: string;
}

interface IModalDTO {
  summary: boolean;
  create: boolean;
  edit: boolean;
}

const masks = new Masks();
export function MyRaffleDefaultTickets() {
  // --- Default Variables --- //
  const {
    actions: { pushToastify },
  } = ZuToastifyStore();

  // --- Page Params --- //
  const [searchParams] = useSearchParams();
  const key = searchParams.get("key");

  // --- Another Control --- //
  const [show, setShow] = useState<{ filter: boolean }>({
    filter: false,
  });

  // --- Modals --- //
  const [affiliateId, setAffiliateId] = useState<number>();
  const [isModalOpen, setIsModalOpen] = useState<IModalDTO>({
    create: false,
    summary: false,
    edit: false,
  });

  // Pagination Part
  const [page, setPage] = useState<number>(1);
  const [take, setTake] = useState<number>(10);
  const [order, setOrder] = useState("DESC");
  const [totalPages, setTotalPages] = useState<number>(1);

  // Filters
  const [searchClient, setSearchClient] = useState<string | undefined>();
  const [searchAffiliate, setSearchAffiliate] = useState<string | undefined>();
  const [client, setClient] = useState<number | undefined>();
  const [campaign, setCampaign] = useState<string | undefined>();
  const [paymentStatus, setPaymentStatus] = useState<
    RaffleDefaultTicketPurchaseStatusTypes | undefined
  >(
    RaffleDefaultTicketPurchaseStatusEnum.PAID as RaffleDefaultTicketPurchaseStatusTypes,
  );
  const [startAt, setStartDate] = useState("");
  const [endAt, setEndDate] = useState("");

  const [resultTime, setResultTime] = useState("Todo Período");

  // API Returns
  const [financialData, setFinancialData] =
    useState<IRaffleDefaultTicketPurchaseSummaryDTO>();
  const [histories, setHistories] =
    useState<IRaffleDefaultTicketPurchaseListDTO[]>();

  const [isLoading, setIsLoading] = useState({
    card: false,
    history: true,
  });

  // History Table List
  async function getSaleTicketHistory({
    page,
    startAt,
    endAt,
  }: IPurchaseHistoryParams) {
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          page: (page || 1).toString(),
          take: take.toString(),
          order,
          ...(key && { raffleKey: key }),
          ...(searchClient && { searchClient }),
          ...(searchAffiliate && { searchAffiliate }),
          ...(paymentStatus && { paymentStatus }),
          ...(startAt && { startAt }),
          ...(endAt && { endAt }),
        }),
      ).toString()}`;
      const result = await api.get(
        `/customer/raffle/default/history/soldTickets${params}`,
      );

      setHistories(result.data.list);
      setTotalPages(result.data.totalPages);
      setIsLoading((prev) => ({ ...prev, history: false }));
    } catch (err: any) {
      setIsLoading((prev) => ({ ...prev, history: false }));
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  }

  // Mini Card of Profits
  async function getFinancialEarningSummary({
    startAt,
    endAt,
  }: IPurchaseSummaryParams) {
    setIsLoading((prev) => ({ ...prev, card: true }));
    try {
      const params = `?${new URLSearchParams(
        Object.entries({
          ...(key && { raffleKey: key }),
          ...(campaign && { campaign }),
          ...(paymentStatus && { paymentStatus }),
          ...(searchClient && { searchClient }),
          ...(searchAffiliate && { searchAffiliate }),
          ...(client && { client: client.toString() }),
          ...(startAt && { startAt }),
          ...(endAt && { endAt }),
        }),
      ).toString()}`;
      const result = await api.get(
        `/customer/financial/history/earningSummary${params}`,
      );

      setFinancialData(result.data);
      setIsLoading((prev) => ({ ...prev, card: false }));
    } catch (err: any) {
      pushToastify({
        message: err?.response?.data.message ?? "Ocorreu um erro!",
        type: "error",
        theme: "dark",
      });
    }
  }

  async function cleanFilters() {
    // --- Main Filters --- //
    setSearchClient(undefined);
    setPaymentStatus(
      RaffleDefaultTicketPurchaseStatusEnum.PAID as RaffleDefaultTicketPurchaseStatusTypes,
    );
    setClient(undefined);
    setCampaign(undefined);
    // --- Anothers --- //
    setStartDate("");
    setEndDate("");
    handleSubmit("", "");
  }

  const handleModal = ({
    opening,
    type,
    affiliateId,
  }: {
    opening: boolean;
    type: "edit" | "create" | "summary";
    affiliateId?: number;
  }) => {
    setAffiliateId(affiliateId);
    setIsModalOpen((prev) => ({
      ...prev,
      summary: type === "summary" ? opening : isModalOpen.summary,
      edit: type === "edit" ? opening : isModalOpen.edit,
      create: type === "create" ? opening : isModalOpen.create,
    }));
  };

  function handleSubmit(_startDate: string, _endDate: string) {
    setIsLoading({ card: true, history: true });
    setResultTime(
      _startDate.length + _endDate.length > 0
        ? `Período Selecionado`
        : `Todo Período`,
    );
    getFinancialEarningSummary({
      startAt: _startDate,
      endAt: _endDate,
    });
    getSaleTicketHistory({
      page,
      startAt: _startDate,
      endAt: _endDate,
    });
  }

  useEffect(() => {
    if (!key) {
      pushToastify({
        message: `A Campanha não foi encontrada.`,
        type: "warning",
        theme: "dark",
      });

      goToHome();
    }
  }, [key]);

  useEffect(() => {
    getFinancialEarningSummary({
      startAt,
      endAt,
    });
    getSaleTicketHistory({
      page,
      startAt,
      endAt,
    });
  }, [page]);

  useEffect(() => {
    getFinancialEarningSummary({
      startAt,
      endAt,
    });
    getSaleTicketHistory({
      page,
      startAt,
      endAt,
    });
  }, []);

  return (
    <>
      {/* Raffle Header → Primary Info */}
      {key && (
        <RafflePrimaryInfo title={"Tickets da Campanha: "} raffleKey={key} />
      )}

      {/* Filters */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div
              className="card-header"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setShow((prev) => ({
                  ...prev,
                  filter: !show.filter,
                }));
              }}
            >
              <div className="d-flex justify-content-between">
                <div className="">
                  <div className="card-title">Filtrar Registros</div>
                  <div className="card-category">
                    Utilize o filtro para trazer um período específico.
                  </div>
                </div>
                <div className="">
                  {show.filter ? (
                    <FaMinus
                      className="fs-5"
                      title="Clique para ocultar as informações."
                    />
                  ) : (
                    <FaExpand
                      className="fs-5"
                      title="Clique para visualizar as informações."
                    />
                  )}
                </div>
              </div>
            </div>
            <div className={`card-body ${show.filter ? `` : `d-none`}`}>
              {/* Another Filters */}
              <div className="row align-items-center mt-2">
                <div className="col-md-3">
                  <Input
                    animated
                    label="Nome ou @arroba do cliente"
                    placeholder=""
                    type="text"
                    value={searchClient || ""}
                    onChange={(e) => {
                      setSearchClient(e.target.value);
                    }}
                    onKeyUp={(e) =>
                      e.key === "Enter" && handleSubmit(startAt, endAt)
                    }
                  />
                </div>
                <div className="col-md-3">
                  <Select
                    animated
                    label="Status da Aquisição"
                    className="form-select form-control"
                    aria-label="paymentStatus"
                    value={paymentStatus || ""}
                    options={[
                      { value: "", name: "Todos" },
                      ...raffleDefaultTicketPurchaseStatusSelect.map((x) => ({
                        value: x.status,
                        name: x.text,
                      })),
                    ]}
                    onChange={(e) => {
                      const value = e.target.value;
                      setPaymentStatus(
                        value === ""
                          ? undefined
                          : (value as RaffleDefaultTicketPurchaseStatusTypes),
                      );
                    }}
                  />
                </div>
                {/* Dates */}
                <div className="col-md-3">
                  <Input
                    animated
                    id="startAt"
                    label="Data Inicial"
                    placeholder=""
                    type="date"
                    value={startAt}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <Input
                    animated
                    id="endAt"
                    label="Data Final"
                    placeholder=""
                    type="date"
                    value={endAt}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="row d-flex justify-content-end mt-4">
                {/* Buttons */}
                <div className="col-md-2">
                  <div className="d-flex justify-content-around">
                    <div className="form-group">
                      <button
                        className="btn btn-sm btn-success form-control rounded px-5"
                        onClick={() => handleSubmit(startAt, endAt)}
                      >
                        Filtrar
                      </button>
                      <small className="text-muted ms-1"> </small>
                    </div>
                    <div className="form-group">
                      <button
                        className="btn btn-sm btn-warning form-control rounded text-white"
                        onClick={cleanFilters}
                        title="Limpar todos os filtros."
                      >
                        <FaBroom />
                      </button>
                      <small className="text-muted ms-1"> </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Report Summary */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="card-title">Resumo dos Proventos</div>
              <div className="card-category">
                Rendimentos, taxas, impostos, comissões, lucro, etc...
              </div>
            </div>
            <div className="card-body mb-3">
              <div className="row g-2 px-2">
                {/* Total Income */}
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="border rounded-5 h-100">
                    <div className="card-header text-center">
                      <div className="fs-5">
                        <div className="">Resumo Geral</div>
                      </div>
                      <div className="text-muted fs-6 p-0 m-0">
                        <small>Total de entradas e vendas geral</small>
                      </div>
                    </div>
                    <div className="card-body">
                      {isLoading.card || !financialData ? (
                        <div className="w-100">
                          <LoadingSkeleton count={2} />
                        </div>
                      ) : (
                        <div className="row d-flex justify-content-between">
                          <div
                            className="col-md-12 col-xxl-4 text-center"
                            title="Total de Ticket(s) vendido(s)."
                          >
                            <span className="fs-4 fw-bold">
                              {masks.formatMilhar(
                                financialData.ticketSoldAmount,
                              )}
                            </span>
                            <br />
                            <span>
                              Ticket
                              {financialData.ticketSoldAmount > 1
                                ? `s`
                                : ``}{" "}
                              <FaCircleInfo />
                            </span>
                          </div>
                          <div className="col-md-12 col-xxl-4 text-center">
                            <span className="fs-4 fw-bold">
                              {masks.brlCurrency(
                                financialData.inflowGrossTotal,
                              )}
                            </span>
                            <br />
                            <span>Bruto</span>
                          </div>
                          <div className="col-md-12 col-xxl-4 text-center">
                            <span className="fs-4 fw-bold">
                              {masks.brlCurrency(financialData.inflowTotal)}
                            </span>
                            <br />
                            <span>Líquido</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Total Affiliate Income */}
                <div className="col-lg-6 col-md-6 col-sm-12">
                  <div className="border rounded-5 h-100">
                    <div className="card-header text-center">
                      <div className="fs-5">
                        <div className="">Resumo de Afiliação</div>
                      </div>
                      <div className="text-muted fs-6 p-0 m-0">
                        <small>Vendas de Afiliados</small>
                      </div>
                    </div>
                    <div className="card-body">
                      {isLoading.card || !financialData ? (
                        <div className="w-100">
                          <LoadingSkeleton count={2} />
                        </div>
                      ) : (
                        <div className="row d-flex justify-content-between">
                          <div className="col-md-12 col-xxl-4 text-center">
                            <span className="fs-4 fw-bold">
                              {masks.formatMilhar(
                                financialData.ticketAffiliateSoldAmount,
                              )}
                            </span>
                            <br />
                            <span>Tickets</span>
                          </div>
                          <div
                            className="col-md-12 col-xxl-4 text-center"
                            title="Entrada bruta por afiliação antes de recolhimento das taxas e comissionamentos."
                          >
                            <span className="fs-4 fw-bold">
                              {masks.brlCurrency(
                                financialData.inflowAffiliateGrossTotal,
                              )}
                            </span>
                            <br />
                            <span>
                              Bruto <FaCircleInfo />
                            </span>
                          </div>
                          <div
                            className="col-md-12 col-xxl-4 text-center"
                            title="Entrada líquida por afiliação após o recolhimento das taxas e comissionamentos."
                          >
                            <span className="fs-4 fw-bold">
                              {masks.brlCurrency(
                                financialData.inflowAffiliateTotal,
                              )}
                            </span>
                            <br />
                            <span>
                              Líquido <FaCircleInfo />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <hr />

              <div className="row g-2 px-2">
                {/* Affiliate's Summary */}
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <div className="border rounded-5 h-100">
                    <div className="card-header text-center">
                      <div className="fs-5">
                        <div className="">Desempenho dos Afiliados</div>
                      </div>
                      <div className="text-muted fs-6 p-0 m-0">
                        <small>Métricas de Geral de Afiliados</small>
                      </div>
                    </div>
                    <div className="card-body">
                      {isLoading.card || !financialData ? (
                        <div className="w-100">
                          <LoadingSkeleton count={2} />
                        </div>
                      ) : (
                        <>
                          {(() => {
                            // --- Treat the Values --- //
                            const ticketFromAffiliatePercent =
                              financialData.ticketSoldAmount > 0
                                ? (financialData.ticketAffiliateSoldAmount /
                                    financialData.ticketSoldAmount) *
                                  100
                                : 0;

                            const profitFromAffiliatePercent =
                              financialData.inflowTotal > 0
                                ? (financialData.inflowAffiliateTotal /
                                    financialData.inflowTotal) *
                                  100
                                : 0;

                            return (
                              <div className="row d-flex justify-content-between">
                                <div
                                  className="col-md-12 col-xxl-4 text-center"
                                  title={"Quantidade de afiliados na Campanha."}
                                >
                                  <span className="fs-4 fw-bold">
                                    {masks.formatMilhar(
                                      financialData.affiliateAmount,
                                    )}
                                  </span>
                                  <br />
                                  <span>
                                    Afiliados <FaCircleInfo />
                                  </span>
                                </div>
                                <div
                                  className="col-md-12 col-xxl-4 text-center"
                                  title="Percentual de tickets vendidos por afiliados."
                                >
                                  <span className="fs-4 fw-bold">
                                    {masks.formatNumber(
                                      ticketFromAffiliatePercent,
                                    )}{" "}
                                    %
                                  </span>
                                  <br />
                                  <span>
                                    Tickets <FaCircleInfo />
                                  </span>
                                </div>
                                <div
                                  className="col-md-12 col-xxl-4 text-center"
                                  title={
                                    "Percentual do Lucro Líquido vindo das vendas de afiliados."
                                  }
                                >
                                  <span className="fs-4 fw-bold">
                                    {masks.formatNumber(
                                      profitFromAffiliatePercent,
                                    )}{" "}
                                    %
                                  </span>
                                  <br />
                                  <span>
                                    Líquido <FaCircleInfo />
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Comissions */}
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <div className="border rounded-5 h-100">
                    <div className="card-header text-center">
                      <div className="fs-5">
                        <div className="">Comissões</div>
                      </div>
                      <div className="text-muted fs-6 p-0 m-0">
                        <small>Repasses para Afiliados</small>
                      </div>
                    </div>
                    <div className="card-body">
                      {isLoading.card || !financialData ? (
                        <div className="w-100">
                          <LoadingSkeleton count={2} />
                        </div>
                      ) : (
                        <div className="row d-flex justify-content-between">
                          <div className="col-md-12 col-xxl-4 text-center">
                            <span className="fs-4 fw-bold">
                              {masks.formatMilhar(
                                Number(financialData.commissionAmount),
                              )}
                            </span>
                            <br />
                            <span>Quantidade</span>
                          </div>
                          <div className="col-md-12 col-xxl-4 text-center">
                            <span className="fs-4 fw-bold">
                              {masks.brlCurrency(
                                Number(financialData.commissionTotal),
                              )}
                            </span>
                            <br />
                            <span>
                              Total{" "}
                              {paymentStatus ===
                              RaffleDefaultTicketPurchaseStatusEnum.REFUND
                                ? `Reembolsado`
                                : `Pago`}
                            </span>
                          </div>
                          <div
                            className="col-md-12 col-xxl-4 text-center"
                            title={"Percentual pago com base no Lucro Líquido."}
                          >
                            <span className="fs-4 fw-bold">
                              {masks.formatNumber(
                                (financialData.commissionTotal * 100) /
                                  financialData.inflowTotal || 0,
                              )}{" "}
                              %
                            </span>
                            <br />
                            <span>
                              Percentual <FaCircleInfo />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Fees Outcomes */}
                <div className="col-lg-4 col-md-6 col-sm-12">
                  <div className="border rounded-5 h-100">
                    <div className="card-header text-center">
                      <div className="fs-5">
                        <div className="">Total de Taxas</div>
                      </div>
                      <div className="text-muted fs-6 p-0 m-0">
                        <small>Taxas Internas, Externas e Outras</small>
                      </div>
                    </div>
                    <div className="card-body">
                      {isLoading.card || !financialData ? (
                        <div className="w-100">
                          <LoadingSkeleton count={2} />
                        </div>
                      ) : (
                        <div className="row d-flex justify-content-between">
                          <div className="col-md-3 text-center">
                            <span className="fs-4 fw-bold">
                              {masks.brlCurrency(
                                Number(financialData.internalFeeTotal),
                              )}
                            </span>
                            <br />
                            <span>
                              Internas{" "}
                              <FaCircleInfo title="Taxas da Plataforma." />
                            </span>
                          </div>
                          <div className="col-md-3 text-center">
                            <span className="fs-4 fw-bold">
                              {masks.brlCurrency(
                                Number(financialData.externalFeeTotal),
                              )}
                            </span>
                            <br />
                            <span>
                              Externas{" "}
                              <FaCircleInfo title="Taxas de provedores, bancos, Gateway de Pagamentos, etc..." />
                            </span>
                          </div>
                          <div className="col-md-3 text-center">
                            <span className="fs-4 fw-bold">
                              {masks.brlCurrency(
                                Number(financialData.anotherFeeTotal),
                              )}
                            </span>
                            <br />
                            <span>
                              Outras{" "}
                              <FaCircleInfo title="Impostos, encargos, etc..." />
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="row d-flex justify-content-end mt-4">
                <div className="col-md-1 text-end">
                  <Button
                    className="btn btn-success rounded w-100"
                    onClick={() => {
                      handleModal({ opening: true, type: "create" });
                    }}
                  >
                    <FaPlus className="fs-5 me-1" /> Afiliado
                  </Button>
                </div>
              </div>
            </div>
            <span className="position-absolute end-0 bg-info text-white p-2 rounded-end rounded-bottom-0">
              {resultTime}
            </span>
          </Card>
        </div>
      </div>

      {/* Table */}
      <div className="row">
        <div className="col-md-12">
          <Card>
            <div className="card-header">
              <div className="row">
                {/* Header of the Table */}
                <div className="col-md-8">
                  <h4 className="card-title">Relação de Vendas de Tickets</h4>
                  <p className="card-category">
                    Vendas de tickets geral desta Campanha
                  </p>
                </div>
                {/* Ordenate Table */}
                <div className="col-md-4">
                  <div className="row align-items-center">
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Quantidade de Registros por Página"
                          value={take}
                          onChange={(e) => setTake(Number(e.target.value))}
                        >
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="75">75</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <select
                          className="form-select"
                          aria-label="Ordenação da Tabela"
                          value={order}
                          onChange={(e) => setOrder(e.target.value)}
                        >
                          <option value="ASC">Crescente</option>
                          <option value="DESC">Decrescente</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <button
                          className="btn btn-sm btn-success form-control px-2"
                          onClick={() => handleSubmit(startAt, endAt)}
                        >
                          Aplicar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <div
                  id="basic-datatables_wrapper"
                  className="dataTables_wrapper container-fluid dt-bootstrap4"
                >
                  <div
                    id="basic-datatables_length"
                    className="dataTables_length"
                  >
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th className="text-center" style={{ width: "60px" }}>
                            #
                          </th>
                          <th>Ticket</th>
                          <th style={{ minWidth: "8rem" }}>Cliente</th>
                          <th>Valor Bruto</th>
                          <th>Tx Interna</th>
                          <th>Tx Externa</th>
                          <th>Tx Outras</th>
                          <th>Comissão</th>
                          <th>Lucro Líquido</th>
                          <th className="text-center">Status</th>
                          <th style={{ minWidth: "9rem" }}>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading.history ? (
                          <tr>
                            <td colSpan={10} className="text-center py-5">
                              <LoadingSkeleton />
                            </td>
                          </tr>
                        ) : histories && histories.length > 0 ? (
                          histories.map((history) => {
                            const paymentStatus =
                              getRaffleDefaultTicketPurchaseStatus(
                                history.status,
                              );
                            const clientName = history?.clientData?.name
                              ? abbreviateName(history?.clientData?.name, 12)
                              : "~";
                            return (
                              <tr key={history.id}>
                                <td className="text-center">{history.id}</td>
                                <td className="text-center">
                                  <span
                                    className="badge bg-light text-dark fs-6"
                                    title={`Ticket id: ${history?.ticketData?.order || 0}`}
                                  >
                                    {history?.ticketData?.ticket || ""}
                                  </span>
                                </td>
                                <td className="">
                                  <div className="d-flex flex-column">
                                    <span>{clientName}</span>
                                    <small className="text-muted">
                                      @{history?.clientData?.atSign || "~"}
                                    </small>
                                  </div>
                                </td>
                                <td className="fw-bold text-primary">
                                  {masks.brlCurrency(history.amountGross)}
                                </td>
                                <td className="text-muted">
                                  {masks.brlCurrency(history.internalFee)}
                                </td>
                                <td className="text-muted">
                                  {masks.brlCurrency(history.externalFee)}
                                </td>
                                <td className="text-muted">
                                  {masks.brlCurrency(history.anotherFee)}
                                </td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span className="text-center">
                                      {masks?.brlCurrency(
                                        history?.commission || 0,
                                      )}
                                      {`${history?.commission > 0 ? ` (${history?.commissionPercent}%)` : ``}`}
                                    </span>
                                    {history?.commissionTo && (
                                      <div
                                        className="text-center"
                                        title={formatName(
                                          history?.commissionToData?.name ||
                                            "~",
                                        )}
                                      >
                                        <span className="badge badge-info fs-6 text-white px-2">
                                          @
                                          {history?.commissionToData?.atSign ||
                                            "~"}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="fw-bold text-success">
                                  {masks.brlCurrency(history.amount)}
                                </td>
                                <td className="text-center">
                                  <span
                                    className={`badge bg-${paymentStatus.color}`}
                                    title={paymentStatus.text}
                                  >
                                    {paymentStatus.title}
                                  </span>
                                </td>
                                <td className="text-muted small">
                                  {dayjs(history.createdAt).format(
                                    "DD/MM/YYYY  [ás] HH:MM:ss",
                                  )}
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={11} className="text-center py-5">
                              <i className="fas fa-inbox fa-3x text-muted mb-3 d-block"></i>
                              <p className="text-muted mb-0">
                                Nenhum registro encontrado.
                              </p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                maxPageShow={3}
              />
            </div>
          </Card>
        </div>
      </div>

      {key && (
        <>
          <ModalRaffleAffiliateCreate
            raffleKey={key}
            isOpen={isModalOpen.create}
            onClose={(created) => {
              handleModal({ opening: false, type: "create" });
              if (created) {
                getSaleTicketHistory({
                  page,
                  startAt,
                  endAt,
                });

                getFinancialEarningSummary({
                  startAt,
                  endAt,
                });
              }
            }}
          />

          {affiliateId && (
            <>
              <ModalRaffleAffiliateSummary
                raffleKey={key}
                affiliateId={affiliateId}
                isOpen={isModalOpen.summary}
                onClose={() => {
                  handleModal({ opening: false, type: "summary" });
                }}
              />

              <ModalRaffleAffiliateEdit
                raffleKey={key}
                affiliateId={affiliateId}
                isOpen={isModalOpen.edit}
                onClose={(updated) => {
                  handleModal({ opening: false, type: "edit" });
                  if (updated) {
                    getSaleTicketHistory({
                      page,
                      startAt,
                      endAt,
                    });
                  }
                }}
              />
            </>
          )}
        </>
      )}
    </>
  );
}
